import { useEffect, useRef, useState } from "react";
import { useAction } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { FunctionReturnType } from "convex/server";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  clearCheckoutReturn,
  getBillingErrorMessage,
  isRetryableBillingError,
  readCheckoutReturn,
} from "@/lib/billing-session";

type CheckoutReturnStatus = FunctionReturnType<typeof api.billing.selfServe.confirmCheckoutReturn>["status"];
export type CheckoutNotice =
  | { status: CheckoutReturnStatus | "confirming" | "canceled" }
  | { status: "error"; message: string };

const CHECKOUT_NOTICES: Record<
  Exclude<CheckoutNotice["status"], "error">,
  { title: string; description: string; variant: "default" | "success" | "warning" | "destructive" }
> = {
  confirming: {
    title: "Confirming your payment",
    description: "This usually takes a few seconds.",
    variant: "default",
  },
  active: {
    title: "Subscription active",
    description: "Your team can now create projects, upload documents, and process work.",
    variant: "success",
  },
  pending: {
    title: "Payment processing",
    description: "Your payment hasn't been confirmed yet. Access starts as soon as it is, and this page updates automatically.",
    variant: "warning",
  },
  payment_failed: {
    title: "Payment didn't go through",
    description: "Update your payment method in Manage billing to keep access.",
    variant: "warning",
  },
  failed: {
    title: "Subscription isn't active",
    description: "This checkout didn't leave you with an active subscription. Start the subscription again when you're ready.",
    variant: "destructive",
  },
  expired: {
    title: "Checkout expired",
    description: "This checkout expired before it was completed. Start the subscription again when you're ready.",
    variant: "default",
  },
  not_completed: {
    title: "Checkout not finished",
    description: "Continue checkout to pick up where you left off. You haven't been charged.",
    variant: "warning",
  },
  canceled: {
    title: "Checkout canceled",
    description: "You haven't been charged.",
    variant: "default",
  },
};

export function CheckoutReturnNotice(props: { notice: CheckoutNotice | null; accessState: string }) {
  if (!props.notice) return null;
  // A pending payment flips to active here as soon as Stripe's webhook lands.
  const status = props.notice.status === "pending" && props.accessState === "paid_active" ? "active" : props.notice.status;
  const content = status === "error"
    ? { title: "Couldn't confirm checkout", description: (props.notice as { message: string }).message, variant: "destructive" as const }
    : CHECKOUT_NOTICES[status];
  return (
    // Errors interrupt; progress and outcomes are announced politely.
    <Alert variant={content.variant} role={content.variant === "destructive" ? "alert" : "status"}>
      {status === "confirming" ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
      <AlertTitle>{content.title}</AlertTitle>
      <AlertDescription>{content.description}</AlertDescription>
    </Alert>
  );
}

/**
 * Confirm a Checkout return with Stripe. The success redirect alone never changes access; the
 * server reads Stripe and applies the authoritative subscription state.
 */
export function useCheckoutReturnNotice(enabled: boolean) {
  const confirmCheckoutReturn = useAction(api.billing.selfServe.confirmCheckoutReturn);
  const [notice, setNotice] = useState<CheckoutNotice | null>(null);
  const handled = useRef(false);

  useEffect(() => {
    if (!enabled || handled.current) return;
    const checkoutReturn = readCheckoutReturn(window.location.search);
    if (!checkoutReturn) return;
    handled.current = true;
    if (checkoutReturn.kind === "canceled") {
      clearCheckoutReturn();
      setNotice({ status: "canceled" });
      return;
    }
    setNotice({ status: "confirming" });
    confirmCheckoutReturn({ sessionId: checkoutReturn.sessionId })
      .then((result) => {
        clearCheckoutReturn();
        setNotice({ status: result.status });
      })
      .catch((error: unknown) => {
        console.error("Confirm checkout error:", error);
        // Keep the session in the URL after a transient failure so a refresh retries it.
        if (!isRetryableBillingError(error)) clearCheckoutReturn();
        setNotice({
          status: "error",
          message: getBillingErrorMessage(error, "We couldn't confirm your checkout yet. Refresh this page in a moment."),
        });
      });
  }, [enabled, confirmCheckoutReturn]);

  return notice;
}
